'use client';

import type { ReactNode } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export type ToastVariant = 'error' | 'success' | 'info';

export interface ToastDescription {
  canDismiss?: boolean;
  content: ReactNode;
  expiresAt: Date;
  id: string;
  title?: string;
  variant?: ToastVariant;
}

export type AddToastOptions = Omit<ToastDescription, 'id' | 'expiresAt'> & { duration?: number; id?: string };

interface ToastState {
  toasts: ToastDescription[];
  addToast: (toast: AddToastOptions) => void;
  removeToast: (toastId: string) => void;
}

export const DEFAULT_TOAST_DURATION = 5_000;

export const useToast = create<ToastState>((set) => ({
  toasts: [],
  addToast: (toast) => {
    set((state) => {
      const { duration, ...toastDescription } = toast;
      const constructedToast = {
        ...toastDescription,
        id: toastDescription.id || Date.now().toString(),
        expiresAt: new Date(Date.now() + (duration || DEFAULT_TOAST_DURATION)),
      };

      if (!state.toasts.find((t) => t.id === constructedToast.id || (t.title === constructedToast.title && t.content === constructedToast.content))) {
        return { toasts: [...state.toasts, constructedToast] };
      }

      return state;
    });
  },
  removeToast: (toastId) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== toastId) }));
  },
}));

export function useAddToast() {
  return useToast(useShallow((state) => state.addToast));
}
